// src/services/api.ts

import axios, { AxiosInstance } from 'axios';
import { Patient, Doctor } from '../../types';
import { Template } from '@pdfme/common';
import { setupInterceptors } from './client/interceptors';
import type { PrescriptionFormData } from '../../hooks/formHooks/usePrescriptionForm';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL!;
const TEMPLATE_API_BASE_URL = process.env.REACT_APP_TEMPLATE_API_URL!;

// Template-specific types
export interface TemplatePayload {
  tempId: string;
  templateSchema: Template;
  fieldSchema: string[];
  name?: string;
  description?: string;
  category?: string;
  isPublic?: boolean;
}

export interface TemplateResponse {
  tempId: string;
  name: string;
  description?: string;
  templateSchema: Template;
  fieldSchema: string[];
  category?: string;
  isPublic?: boolean;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  count?: number;
}

export interface CertificateRecord {
  certificate_id: string;
  certificate_type: string;
  uploaded_at: string;
}

export interface UnpaidCase {
  doc_id: string;
  full_name: string;
  certificate_type: string;
  status: string;
  billable_since: string;
}

export interface BillingDoctorEntry {
  doctor_id: string;
  name: string;
  specialization: string;
  regNo: string;
  rate: number;
  status_breakdown: Record<string, number>;
  unpaid_case_count: number;
  current_balance: number;
  total_paid_to_date: number;
  last_paid_at: string | null;
  unpaid_cases: UnpaidCase[];
}

export interface BillingSummary {
  default_rate: number;
  totals: {
    doctor_count: number;
    total_unpaid_cases: number;
    total_outstanding: number;
  };
  doctors: BillingDoctorEntry[];
}

export interface DoctorPaymentRecord {
  id: string;
  doctor_id: string;
  case_count: number;
  amount: number;
  paid_at: string;
  note: string;
}

class ApiService {
  private api: AxiosInstance;
  private templateApi: AxiosInstance;

  constructor() {
    // Main API instance (patients, doctors, etc.)
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    // Template API instance (separate base URL)
    this.templateApi = axios.create({
      baseURL: TEMPLATE_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    setupInterceptors(this.api);
    setupInterceptors(this.templateApi);


    this.getPendingPatients = this.getPendingPatients.bind(this);
    this.getPatientsByStatus = this.getPatientsByStatus.bind(this);
    this.getPatients = this.getPatients.bind(this);
    this.getPatient = this.getPatient.bind(this);
    this.createPatient = this.createPatient.bind(this);
    this.updatePatient = this.updatePatient.bind(this);
    this.deletePatient = this.deletePatient.bind(this);
    this.updatePatientStatus = this.updatePatientStatus.bind(this);

    this.getDoctors = this.getDoctors.bind(this);
    this.getDoctor = this.getDoctor.bind(this);
    this.getDoctorSignatureUrl = this.getDoctorSignatureUrl.bind(this);
    this.uploadDoctorSignature = this.uploadDoctorSignature.bind(this);
    this.createDoctor = this.createDoctor.bind(this);
    this.updateDoctor = this.updateDoctor.bind(this);
    this.deleteDoctor = this.deleteDoctor.bind(this);

    this.createTemplate = this.createTemplate.bind(this);
    this.getTemplate = this.getTemplate.bind(this);
    this.getTemplates = this.getTemplates.bind(this);
    this.getTemplatesByCategory = this.getTemplatesByCategory.bind(this);
    this.updateTemplate = this.updateTemplate.bind(this);
    this.deleteTemplate = this.deleteTemplate.bind(this);
    this.checkTemplateExists = this.checkTemplateExists.bind(this);
    this.assignDoctor = this.assignDoctor.bind(this);
    this.getAssignedCases = this.getAssignedCases.bind(this);
    this.generateDoctorLink = this.generateDoctorLink.bind(this);
    this.validateDoctorToken = this.validateDoctorToken.bind(this);
    this.getSubmission = this.getSubmission.bind(this);
    this.saveCertificate = this.saveCertificate.bind(this);
    this.getCertificate = this.getCertificate.bind(this);
    this.savePrescription = this.savePrescription.bind(this);
    this.getCertificateUploadUrl = this.getCertificateUploadUrl.bind(this);
    this.confirmCertificateUpload = this.confirmCertificateUpload.bind(this);
    this.listPatientCertificates = this.listPatientCertificates.bind(this);
    this.getCertificateDownloadUrl = this.getCertificateDownloadUrl.bind(this);

    this.getBillingSummary = this.getBillingSummary.bind(this);
    this.recordDoctorPayment = this.recordDoctorPayment.bind(this);
    this.getDoctorPayments = this.getDoctorPayments.bind(this);
    this.setDoctorRate = this.setDoctorRate.bind(this);
    this.getBillingRate = this.getBillingRate.bind(this);
    this.setBillingRate = this.setBillingRate.bind(this);
  }

  // ==================== Patient APIs ====================

  async getPendingPatients(): Promise<Patient[]> {
    const response = await this.api.get('/patients/status/pending');
    return response.data.data;
  }

  async getPatientsByStatus(
    status: string,
    { limit = 50, cursor }: { limit?: number; cursor?: string | null } = {}
  ): Promise<{
    data: Patient[];
    count: number;
    nextCursor: string | null;
    hasMore: boolean;
  }> {
    const url = cursor
      ? `/patients/status/${status}?limit=${limit}&cursor=${encodeURIComponent(cursor)}`
      : `/patients/status/${status}?limit=${limit}`;
    const response = await this.api.get(url);
    const { data, count, nextCursor, hasMore } = response.data;
    return { data, count, nextCursor: nextCursor ?? null, hasMore: !!hasMore };
  }

  async getPatients({ limit = 50, cursor }: { limit?: number; cursor?: string | null } = {}): Promise<{
    data: Patient[];
    count: number;
    nextCursor: string | null;
    hasMore: boolean;
  }> {
    const url = cursor
      ? `/patients?limit=${limit}&cursor=${encodeURIComponent(cursor)}`
      : `/patients?limit=${limit}`;
    const response = await this.api.get(url);
    const { data, count, nextCursor, hasMore } = response.data;
    return { data, count, nextCursor: nextCursor ?? null, hasMore: !!hasMore };
  }

  async getPatient(docId: string): Promise<Patient> {
    const response = await this.api.get(`/patients/${docId}`);
    return response.data.data;
  }

  async createPatient(data: Partial<Patient>): Promise<{ doc_id: string }> {
    const response = await this.api.post('/patients', data);
    return response.data.data;
  }

  async updatePatient(docId: string, data: Partial<Patient>): Promise<void> {
    await this.api.put(`/patients/${docId}`, data);
  }

  async deletePatient(docId: string): Promise<void> {
    await this.api.delete(`/patients/${docId}`);
  }

  async updatePatientStatus(docId: string, status: 'pending' | 'completed' | 'cancelled'|'assigned'): Promise<void> {
    await this.api.put(`/patients/${docId}/status`, { status });
  }
  // ==================== Doctor APIs ====================

  // List all doctors — filter client-side to SK=METADATA items only, since the
  // Doctors table also holds RATE and CONFIG items that the backend scan returns.
  async getDoctors(): Promise<Doctor[]> {
    const response = await this.api.get('/doctors');
    // response.data = { success, data, count }
    const all = response.data.data as Doctor[];
    return all.filter((d) => d.SK === 'METADATA' && d.id);
  }

  // Get single doctor by id
  async getDoctor(id: string): Promise<Doctor> {
    const response = await this.api.get(`/doctors/${id}`);
    return response.data.data as Doctor;
  }
  async getDoctorSignatureUrl(doctorId: string): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/doctors/${doctorId}/signature`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch signature URL');
    }

    return data.data.signatureUrl;
  }
  /**
   * Uploads a signature image straight to S3 via a presigned URL and returns
   * the resulting signatureKey, which should then be passed to
   * createDoctor / updateDoctor.
   *
   * doctorId is optional — omit it when creating a doctor that doesn't exist yet.
   */
  async uploadDoctorSignature(file: File, doctorId?: string): Promise<string> {
    const response = await this.api.post('/doctors/signature/upload-url', {
      content_type: file.type,
      ...(doctorId ? { doctor_id: doctorId } : {}),
    });

    const { uploadUrl, signatureKey } = response.data.data as {
      uploadUrl: string;
      signatureKey: string;
    };

    // Raw fetch, not this.api — the presigned URL carries its own auth and
    // must not receive the app's Authorization header.
    const upload = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });

    if (!upload.ok) {
      throw new Error('Failed to upload signature image');
    }

    return signatureKey;
  }

  // Create doctor
  async createDoctor(
    data: Pick<Doctor, 'name' | 'regNo' | 'specialization'> & Partial<Doctor>
  ): Promise<{ id: string }> {
    const response = await this.api.post('/doctors', data);
    // response.data.data = { id, name, regNo, specialization, signatureKey, signatureUrl, createdAt }
    return response.data.data as { id: string };
  }

  // Update doctor (name / regNo / specialization / signatureKey)
  async updateDoctor(
    id: string,
    data: Partial<Pick<Doctor, 'name' | 'regNo' | 'specialization'>> & { signatureKey?: string }
  ): Promise<void> {
    await this.api.put(`/doctors/${id}`, data);
  }

  // Delete doctor
  async deleteDoctor(id: string): Promise<void> {
    await this.api.delete(`/doctors/${id}`);
  }
  // ==================== Template APIs ====================

  /**
   * Create a new template
   */
  async createTemplate(payload: TemplatePayload): Promise<ApiResponse<any>> {
    try {
      const response = await this.templateApi.post('/templates', payload);
      return response.data;
    } catch (error: any) {
      console.error('Error creating template:', error);
      throw error;
    }
  }

  /**
   * Get template by ID
   */
  async getTemplate(tempId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.templateApi.get(`/templates/${tempId}`);

      return response.data;
    } catch (error: any) {
      console.error('❌ getTemplate: Error -', error);
      throw error;
    }
  }

  /**
   * Get all templates (optionally filter by category)
   */
  async getTemplates(category?: string): Promise<ApiResponse<any>> {
    try {
      const url = category ? `/templates?category=${category}` : '/templates';
      const response = await this.templateApi.get(url);
      return response.data;
    } catch (error: any) {
      console.error('Error getting templates:', error);
      throw error;
    }
  }

  /**
   * Get templates by category (alternative method)
   */
  async getTemplatesByCategory(category: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.templateApi.get(`/templates/category/${category}`);
      return response.data;
    } catch (error: any) {
      console.error('Error getting templates by category:', error);
      throw error;
    }
  }

  /**
   * Update template
   */
  async updateTemplate(
    tempId: string,
    payload: Partial<TemplatePayload>
  ): Promise<ApiResponse<TemplateResponse>> {
    try {
      const response = await this.templateApi.put(`/templates/${tempId}`, payload);
      return response.data;
    } catch (error: any) {
      console.error('Error updating template:', error);
      throw error;
    }
  }

  /**
   * Delete template (soft delete by default)
   */
  async deleteTemplate(tempId: string, hardDelete = false): Promise<ApiResponse<void>> {
    try {
      const url = hardDelete ? `/templates/${tempId}?hard=true` : `/templates/${tempId}`;
      const response = await this.templateApi.delete(url);
      return response.data;
    } catch (error: any) {
      console.error('Error deleting template:', error);
      throw error;
    }
  }

  /**
   * Check if template exists
   */
  async checkTemplateExists(tempId: string): Promise<boolean> {
    try {
      const response = await this.templateApi.head(`/templates/${tempId}`);
      return response.status === 200;
    } catch (error) {
      console.error('Error checking template:', error);
      return false;
    }
  }
async assignDoctor(docId: string, doctorId: string): Promise<void> {
  await this.api.patch(`/patients/${docId}/assign`, { assigned_doctor_id: doctorId });
}

// ─────────────────────────────────────────────────────────────
// GET CASES ASSIGNED TO A SPECIFIC DOCTOR
// Used by DoctorDashboard to filter sidebar
//
// Backend should:
//   Query GSI where assigned_doctor_id = doctorId
//   (or filter in-memory if your table is small enough)
// ─────────────────────────────────────────────────────────────

  async getAssignedCases(doctorId: string): Promise<any[]> {
    const res = await fetch(`${API_BASE_URL}/patients/assigned?assigned_doctor_id=${doctorId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) {
      throw new Error('Failed to load assigned cases');
    }

    const data = await res.json();
    return data.patients ?? data ?? [];
  }

  async generateDoctorLink(doctorId: string): Promise<{
    link: string;
    whatsapp_url: string;
    token: string;
    doctor_name: string;
    expires_at: string;
    expires_in_minutes: number;
  }> {
    const res = await this.api.post(`/doctors/${doctorId}/generate-link`);
    return res.data.data;
  }

  async validateDoctorToken(token: string): Promise<{
    doctor: { id: string; name: string; specialization: string; regNo: string; signatureUrl: string | null };
    patients: Patient[];
    token_expires_at: string;
    token_minutes_remaining: number;
  }> {
    const res = await fetch(`${API_BASE_URL}/doctor-access?token=${encodeURIComponent(token)}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Invalid or expired link');
    return data.data;
  }

  async getSubmission(docId: string): Promise<{
    prescription: PrescriptionFormData;
    submitted_at: string;
    certificate: any | null;
    certificate_saved_at: string | null;
  }> {
    const response = await this.api.get(`/patients/${docId}/submission`);
    return response.data.data;
  }

  async saveCertificate(docId: string, data: Record<string, any>): Promise<void> {
    await this.api.put(`/patients/${docId}/certificate`, data);
  }

  async getCertificate(docId: string): Promise<{ certificate: any; updated_at: string } | null> {
    try {
      const response = await this.api.get(`/patients/${docId}/certificate`);
      return response.data.data;
    } catch {
      return null;
    }
  }

  async savePrescription(docId: string, data: Record<string, any>): Promise<void> {
    await this.api.put(`/patients/${docId}/prescription`, data);
  }

  async getCertificateUploadUrl(docId: string, certificateType: string): Promise<{
    upload_url: string;
    certificate_id: string;
  }> {
    const response = await this.api.post(`/patients/${docId}/certificates/upload-url`, { certificate_type: certificateType });
    return response.data.data;
  }

  async confirmCertificateUpload(docId: string, certificateId: string, certificateType: string): Promise<void> {
    await this.api.post('/certificates/confirm', { doc_id: docId, certificate_id: certificateId, certificate_type: certificateType });
  }

  async listPatientCertificates(docId: string): Promise<CertificateRecord[]> {
    const response = await this.api.get(`/patients/${docId}/certificates`);
    return response.data.data;
  }

  async getCertificateDownloadUrl(docId: string, certificateId: string): Promise<{ download_url: string }> {
    const response = await this.api.get(`/certificates/${certificateId}/download?doc_id=${encodeURIComponent(docId)}`);
    return response.data.data;
  }

  // ==================== Billing APIs ====================

  async getBillingSummary(): Promise<BillingSummary> {
    const response = await this.api.get('/doctors/payments/summary');
    return response.data.data;
  }

  async recordDoctorPayment(doctorId: string, payload: { amount: number; note: string }): Promise<void> {
    await this.api.post(`/doctors/${doctorId}/payments`, payload);
  }

  async getDoctorPayments(doctorId: string): Promise<DoctorPaymentRecord[]> {
    const response = await this.api.get(`/doctors/${doctorId}/payments`);
    return response.data.data;
  }

  async setDoctorRate(doctorId: string, rate: number | null): Promise<void> {
    await this.api.put(`/doctors/${doctorId}/rate`, { rate });
  }

  async getBillingRate(): Promise<number> {
    const response = await this.api.get('/billing/rate');
    return response.data.data.rate;
  }

  async setBillingRate(rate: number): Promise<void> {
    await this.api.put('/billing/rate', { rate });
  }

}

// ✅ Create singleton instance
export const apiService = new ApiService();

// ✅ Export individual methods bound to the instance
export const {
  // Patient methods
  getPendingPatients,
  getPatientsByStatus,
  getPatients,
  getPatient,
  createPatient,
  updatePatient,
  deletePatient,
  updatePatientStatus,

  // Doctor methods
  getDoctors,
  getDoctor,
  getDoctorSignatureUrl,
  uploadDoctorSignature,
  createDoctor,
  updateDoctor,
  deleteDoctor,
  assignDoctor,
  getAssignedCases,
  generateDoctorLink,
  validateDoctorToken,
  getSubmission,
  saveCertificate,
  getCertificate,
  savePrescription,
  getCertificateUploadUrl,
  confirmCertificateUpload,
  listPatientCertificates,
  getCertificateDownloadUrl,

  // Billing methods
  getBillingSummary,
  recordDoctorPayment,
  getDoctorPayments,
  setDoctorRate,
  getBillingRate,
  setBillingRate,

  // Template methods
  createTemplate,
  getTemplate,
  getTemplates,
  getTemplatesByCategory,
  updateTemplate,
  deleteTemplate,
  checkTemplateExists,
} = apiService;